import i18n from "../i18n";
import { publicFileUrl } from "../utils/publicFileUrl";
import _ from "lodash";
import React, { useState } from "react";
import { useServerApi } from "../hooks/userServerApi";
import { openWhatsapp } from "../hooks/useCellRender";
import { formatPhoneDisplay } from "../helpers/phoneUtils";
import {
  createStyles,
  Avatar,
  Text,
  Group,
  Badge,
  UnstyledButton,
  Tooltip,
  Stack,
} from "@mantine/core";
import {
  IconPhoneCall,
  IconAt,
  IconAlertTriangle,
  IconMail,
  IconCircleCheck,
} from "@tabler/icons";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { useTranslation } from "react-i18next";
import {
  buildConsultantLicenceErrorMessage,
  getProductTypeId,
} from "../helpers/consultantLicenceUtils";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";

const useStyles = createStyles((theme) => ({
  icon: {
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[3]
        : theme.colors.gray[5],
  },

  name: {
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
  },
}));

export function UserCard({ value = {}, ...props }) {
  const { classes } = useStyles();
  const {
    image,
    name,
    cname,
    workTitle,
    phone,
    email,
    aliasName,
    staffId,
  } = value ?? {};

  const displayName = [name, cname, aliasName].filter(Boolean).join(", ");

  return (
    <Group noWrap align="flex-start" sx={{ width: "100%" }} {...props}>
      <Avatar
        src={publicFileUrl(image)}
        size={80}
        radius="md"
        sx={{ flexShrink: 0 }}
      />
      <Stack
        spacing={4}
        sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        style={{ flexGrow: 1 }}
      >
        {workTitle && (
          <Text
            size="xs"
            sx={{ textTransform: "uppercase" }}
            color="dimmed"
            lineClamp={1}
          >
            {workTitle}
          </Text>
        )}

        <Tooltip label={displayName} disabled={!displayName} multiline maw={320}>
          <EntityTabAnchor
            entity="user"
            id={value._id}
            label={displayName}
            fallback={displayName}
            size="sm"
            weight={600}
            underline={false}
            className={classes.name}
            sx={{
              lineHeight: 1.3,
              display: "block",
              textDecoration: "none",
              cursor: "pointer",
              color: "inherit",
              "&:hover": {
                textDecoration: "none",
                color: "inherit",
              },
            }}
          />
        </Tooltip>

        {email && (
          <Group noWrap spacing={5} align="flex-start" sx={{ minWidth: 0 }}>
            <IconMail
              stroke={1.5}
              size={16}
              className={classes.icon}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Tooltip label={email} multiline maw={320}>
              <Text
                size="xs"
                color="dimmed"
                lineClamp={2}
                sx={{ wordBreak: "break-all", flex: 1, minWidth: 0 }}
              >
                {email}
              </Text>
            </Tooltip>
          </Group>
        )}

        {phone && (
          <Group spacing={5} noWrap sx={{ minWidth: 0 }}>
            <IconPhoneCall
              stroke={1.5}
              size={16}
              className={classes.icon}
              style={{ flexShrink: 0 }}
            />
            <UnstyledButton sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip label={formatPhoneDisplay(phone)}>
                <Text
                  size="xs"
                  color="dimmed"
                  lineClamp={1}
                  onClick={() => openWhatsapp(phone)}
                  sx={{ wordBreak: "break-all" }}
                >
                  {formatPhoneDisplay(phone)}
                </Text>
              </Tooltip>
            </UnstyledButton>
          </Group>
        )}

        {staffId && (
          <Group spacing={5} noWrap sx={{ flexShrink: 0 }}>
            <IconAt stroke={1.5} size={16} className={classes.icon} />
            <Text size="xs" color="dimmed" lineClamp={1}>
              {staffId}
            </Text>
          </Group>
        )}
      </Stack>
    </Group>
  );
}

const ConsultantList = ({
  form,
  consultantIds,
  showClientTransfer = false,
  ...props
}) => {
  const ids = consultantIds || _.get(form?.values, props.field);
  const isConsultantField = props.field === "consultants";
  const [api] = useServerApi();
  const [consultants, setConsultants] = useState([]);
  const [clientTrans, setClientTrans] = useState(null);
  const [licenceByConsultant, setLicenceByConsultant] = useState({});
  const { t } = useTranslation();

  const apiEntity = "user";
  const productTypeId = getProductTypeId(form?.values);

  const fetchData = async () => {
    try {
      if (!_.isArray(ids) || _.isEmpty(ids)) return setConsultants([]);
      const carr = [];

      if (showClientTransfer) {
        setClientTrans(_.get(form.values, "isTransfer"));
      }

      for (const id of ids) {
        if (typeof id === "string") {
          const c = await api.getById({ apiEntity, id });
          if (c) carr.push(c);
          if (c && c?.disableCreateOrder) {
            const message = "Consultant is not allowed to create new order.";
            if (form.values.error !== message) {
              form.setFieldValue("error", message);
            }
          }
        } else if (_.isObject(id)) {
          carr.push(id);
        }
      }
      setConsultants(carr);
    } catch (e) {
      setConsultants([]);
    }
  };

  useDeepCompareEffect(() => {
    fetchData();
  }, [ids]);

  useDeepCompareEffect(() => {
    const runLicenceChecks = async () => {
      if (!isConsultantField || !form) return;

      if (!productTypeId || _.isEmpty(consultants)) {
        form.setFieldValue("checkConsultantLicenceOnConfirm", false);
        form.setFieldValue("consultantLicenceError", null);
        form.setFieldValue("consultantLicenceChecking", false);
        setLicenceByConsultant({});
        form.validate?.();
        return;
      }

      form.setFieldValue("consultantLicenceChecking", true);
      form.validate?.();

      let checkEnabled = false;
      const unlicensedNames = [];
      const nextLicenceStatus = {};

      for (const consultant of consultants) {
        if (!consultant?._id) continue;

        if (consultant.userType === "CHANNEL") {
          nextLicenceStatus[consultant._id] = { hasLicence: true };
          continue;
        }

        const result = await api.User.checkLicence(
          consultant._id,
          productTypeId
        );
        if (result?.checkConsultantLicenceOnConfirm) {
          checkEnabled = true;
        }

        const hasLicence = result?.hasLicence ?? true;
        nextLicenceStatus[consultant._id] = { hasLicence };

        if (!hasLicence) {
          unlicensedNames.push(
            consultant.name || consultant.staffId || consultant._id
          );
        }
      }

      setLicenceByConsultant(nextLicenceStatus);
      form.setFieldValue("checkConsultantLicenceOnConfirm", checkEnabled);
      form.setFieldValue("consultantLicenceChecking", false);

      if (checkEnabled && unlicensedNames.length > 0) {
        form.setFieldValue(
          "consultantLicenceError",
          buildConsultantLicenceErrorMessage(unlicensedNames, t)
        );
      } else {
        form.setFieldValue("consultantLicenceError", null);
      }

      form.validate?.();
    };

    runLicenceChecks();
  }, [consultants, productTypeId, isConsultantField]);

  return (
    <>
      {!_.isEmpty(consultants) && (
        <Stack spacing="md">
          {consultants.map(
            (consultant, index) =>
              consultant && (
                <div key={consultant._id ?? index} style={{ minWidth: 0 }}>
                  <Group mb={"xs"} spacing={3}>
                    {index == 0 ? (
                      <Badge size="xs" color={"green"} radius={0}>{i18n.t("Primary")}</Badge>
                    ) : (
                      <Badge size="xs" color={"red"} radius={0}>{i18n.t("Secondary")}</Badge>
                    )}

                    {clientTrans !== null &&
                      (clientTrans ? (
                        <Badge size="xs" radius={0} color="red">
                          {i18n.t("Transfer")}
                        </Badge>
                      ) : (
                        <Badge size="xs" radius={0}>{i18n.t("Direct")}</Badge>
                      ))}

                    {isConsultantField &&
                      productTypeId &&
                      consultant.userType !== "CHANNEL" &&
                      licenceByConsultant[consultant._id] && (
                        <>
                          {!licenceByConsultant[consultant._id].hasLicence && (
                            <Badge color={"blue"} size="xs" radius={0}>{i18n.t("Referer")}</Badge>
                          )}
                          {licenceByConsultant[consultant._id].hasLicence && (
                            <Badge
                              color={"green"}
                              size="xs"
                              radius={0}
                              leftSection={
                                <IconCircleCheck
                                  size={18}
                                  style={{ paddingTop: 5 }}
                                />
                              }
                            >{i18n.t("Licences")}</Badge>
                          )}
                        </>
                      )}
                  </Group>
                  <UserCard value={consultant} />
                  {consultant?.disableCreateOrder && (
                    <Group mt="xl">
                      <IconAlertTriangle color="red" />
                      <Text size="xs">
                        {t(
                          "Sorry, this consultant is not allowed to create new order"
                        )}
                      </Text>
                    </Group>
                  )}
                  {isConsultantField &&
                    productTypeId &&
                    licenceByConsultant[consultant._id] &&
                    !licenceByConsultant[consultant._id].hasLicence && (
                      <Group mt="xs">
                        <IconAlertTriangle color="orange" size={15} />
                        <Text size="xs" color="orange">
                          {t(
                            "This consultant does not have the required licence for the selected product type"
                          )}
                        </Text>
                      </Group>
                    )}
                </div>
              )
          )}
        </Stack>
      )}
    </>
  );
};

export default ConsultantList;
